import {
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Modal,
  Table,
} from 'react-bootstrap'
import Select from 'react-select'
import { sortObjectsByKey } from '@common/utils/helperFunctions'
import {
  useHistoricalAlertGroupQuery,
  useHistoricalAlertsQuery,
} from '@data/alerts/alerts'
import {
  useDevicesLoading,
  useVisibleDevices,
} from '@data/device/device'
import DateRangeComponent from '../../main/Components/Calendar/DateRangeSelector'
import {
  HistoricalAlert,
  HistoricalAlertTier,
  HistoricalGroupSelection,
  Stats,
} from '@data/alerts/model/alert.model'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import AlertQueueItem from './AlertQueueItem'
import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip,
} from 'chart.js'
import HistoricalStats from '../Components/HistoricalAlertsStats/HistoricalAlertsStats'
import { buildPDFReport } from './helper-functions'
import { useSelectedAccounts } from '@data/accounts/accounts'
import { format } from 'date-fns'
import { TZDate } from '@date-fns/tz'

ChartJS.register(ArcElement, Tooltip, Legend)

// A stable reference for the pending/errored state of the history query. An inline
// `data: historical = []` builds a new array on every render while `data` is undefined,
// which invalidates the memos below on every render for no reason.
//
// It used to be worse than wasted work: while these values were state written from
// effects, the unstable identity re-fired both effects every render and the page threw
// "Maximum update depth exceeded" until the request resolved. The memos make that
// impossible now, so this constant is an optimisation rather than the fix.
const NO_HISTORICAL_ALERTS: HistoricalAlert[] = []

export default function HistoricalAlertView() {
  const devices = useVisibleDevices()
  const loadingDevices = useDevicesLoading()
  const {
    data: historical = NO_HISTORICAL_ALERTS,
    isError,
    isLoading,
  } = useHistoricalAlertsQuery({ ackResult: [1, 2, 3] })
  const selectedAccounts = useSelectedAccounts()
  const [selectedHistoricalGroups, setSelectedHistoricalGroups] = useState<HistoricalGroupSelection[]>([])
  const [selectedHistoricalGroup, setSelectedHistoricalGroup] = useState<HistoricalGroupSelection>()
  const [sortingConf, setSortingConf] = useState({ key: '', order: '' })
  const [fromDate, setFromDate] = useState<Date | null>(null)
  const [toDate, setToDate] = useState<Date | null>(null)

  const [searchTerm, setSearchTerm] = useState('')

  const tableRef = useRef(null)
  const totalChartRef = useRef(null)
  const top5ChartRef = useRef(null)

  // Both of these are derived from the history query and the current filters, so they are
  // computed rather than stored. As state written from effects they cost a second render
  // per change, and a render loop whenever an input's identity was unstable.
  // sortObjectsByKey is loosely typed, so annotate here: this used to be pinned by
  // useState<HistoricalAlert[]>, and without it every downstream callback infers `any`.
  const filtered = useMemo<HistoricalAlert[]>(() => {
    const sortedObjects: HistoricalAlert[] = sortObjectsByKey(
      historical.filter((h) => {
        const device = devices.find((d) => d.dUuid === h.dUuid)
        return (
          device &&
          (searchTerm.length === 0 ||
            h.deviceId.includes(searchTerm) ||
            device.deviceName.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()))
        )
      }),
      sortingConf.key,
      sortingConf.order,
    )

    return fromDate && toDate
      ? filterObjectsByDateRange(sortedObjects, fromDate, toDate)
      : sortedObjects
  }, [devices, searchTerm, historical, sortingConf, fromDate, toDate])

  // `historical` was in the old effect's dependencies but never read by it — the reduce
  // shadowed the name with its own parameter, since renamed. `filtered` already covers it.
  const statsTotal = useMemo<Stats>(
    () =>
      filtered
        .filter((h) => !!devices.find((d) => d.dUuid === h.dUuid))
        .reduce(
          (accumulator, entry: HistoricalAlert) => {
            return {
              tier1Total: accumulator.tier1Total + entry.tier1.length,
              tier2Total: accumulator.tier2Total + entry.tier2.length,
              tier3Total: accumulator.tier3Total + entry.tier3.length,
              totalAll:
                accumulator.totalAll +
                entry.tier1.length +
                entry.tier2.length +
                entry.tier3.length,
            }
          },
          { tier1Total: 0, tier2Total: 0, tier3Total: 0, totalAll: 0 } as Stats,
        ),
    [devices, filtered],
  )

  const selectedGroupDevice = selectedHistoricalGroup
    ? devices.find((d) => d.dUuid === selectedHistoricalGroup.dUuid)
    : undefined

  const {
    data: selectedAlertGroup,
    isError: isGroupError,
    isLoading: isGroupLoading,
  } = useHistoricalAlertGroupQuery(
    selectedHistoricalGroup && selectedGroupDevice
      ? {
        dUuid: selectedHistoricalGroup.dUuid,
        gid: selectedHistoricalGroup.group.gid,
        uom: selectedHistoricalGroup.deviceUom,
      }
      : undefined,
    selectedGroupDevice
      ? {
        deviceId: selectedGroupDevice.deviceId,
        deviceName: selectedGroupDevice.deviceName,
        uom: selectedGroupDevice.deviceSettings.uom,
      }
      : undefined,
    selectedHistoricalGroup?.priorityTier,
  )

  function onColumnHeaderClick(columnKey: string) {
    let order: string
    if (sortingConf.key === columnKey) {
      if (sortingConf.order === '') {
        order = 'DESC'
      } else if (sortingConf.order === 'DESC') {
        order = 'ASC'
      } else {
        order = ''
      }
    } else {
      order = 'DESC'
    }
    setSortingConf({ key: columnKey, order })
  }

  function getSortingClass(columnKey: string) {
    return sortingConf.key === columnKey && sortingConf.order !== ''
      ? `fas fa-arrow-${sortingConf.order === 'ASC' ? 'down' : 'up'}`
      : ''
  }

  function filterTiersByDateRange(
    tiers: HistoricalAlertTier[],
    start: Date,
    end: Date,
  ): HistoricalAlertTier[] {
    return tiers.filter((t: HistoricalAlertTier) => {
      const dateInTier = new Date(t.startDate)
      return dateInTier >= start && dateInTier <= end
    })
  }

  function filterObjectsByDateRange(alerts: HistoricalAlert[], start: Date, end: Date) {
    return alerts
      .map((obj) => ({
        ...obj,
        tier1: filterTiersByDateRange(obj.tier1, start, end),
        tier2: filterTiersByDateRange(obj.tier2, start, end),
        tier3: filterTiersByDateRange(obj.tier3, start, end),
      }))
      .filter((obj) => obj.tier1.length > 0 || obj.tier2.length > 0 || obj.tier3.length > 0)
  }

  const exportPageAsPdf = async () => {
    const tableHTML = tableRef.current
    if (!totalChartRef) return
    if (!totalChartRef.current) return
    if (!top5ChartRef) return
    if (!top5ChartRef.current) return
    if (!tableHTML) return

    const accountName = selectedAccounts[0].accountName

    const totalChartCanva = await html2canvas(totalChartRef.current)
    const totalChartImage = totalChartCanva.toDataURL('image/png')

    const top5ChartCanva = await html2canvas(top5ChartRef.current)
    const top5ChartImage = top5ChartCanva.toDataURL('image/png')

    buildPDFReport(accountName, fromDate, toDate, totalChartImage, top5ChartImage, tableHTML).then(
      (pdf: jsPDF) => {
        pdf.save(`${accountName}_Alert_History_${format(new Date(), 'dd-MM-yyyy_HH:mm')}`)
      },
    )
  }

  if (isLoading || loadingDevices) {
    return <div className='spinner-border m-auto mb-5 mt-5 d-flex text-primary' />
  }

  if (isError) {
    return <h2 className='mt-5 text-center'>Unable to load historical alerts</h2>
  }

  return (
    <>
      <h1 className='mt-4 mb-4'>Alert History</h1>

      <div>
        {selectedAccounts.length === 1 && (
          <div className='d-flex justify-content-end'>
            <button className='btn btn-primary mb-4' onClick={() => exportPageAsPdf()}>
              Export page as PDF
            </button>
          </div>
        )}

        <div style={{ width: '100%' }}>
          <div className='input-group mb-3'>
            <div className='form-group search-bar'>
              <span className='fa fa-search search-icon'></span>
              <input
                type='text'
                className='form-control'
                placeholder='Search by device name or ID'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span className='fa fa-times clear-icon' onClick={() => setSearchTerm('')}></span>
            </div>
            <div className='input-group-append result-number'>
              <span>( {filtered.length} )</span>
            </div>
          </div>
        </div>

        <div className='col-md-3 col-sm-12 mb-3 p-0'>
          <DateRangeComponent
            from={fromDate}
            to={toDate}
            fromCallback={setFromDate}
            toCallback={setToDate}
            label={'Date Range'}
            isClearable
          />
        </div>
      </div>

      {historical.length > 0 ? (
        <div>
          <HistoricalStats
            historical={filtered}
            totalChartRef={totalChartRef}
            top5ChartRef={top5ChartRef}
          />
          <div>
            <Table ref={tableRef} responsive>
              <thead>
                <tr>
                  <th>Device Location</th>
                  <th
                    className='clickable'
                    style={{ width: '15%', textAlign: 'center' }}
                    onClick={() => onColumnHeaderClick('tier1')}
                  >
                    <i className={getSortingClass('tier1')}></i>Tier 1
                  </th>
                  <th
                    className='clickable'
                    style={{ width: '15%', textAlign: 'center' }}
                    onClick={() => onColumnHeaderClick('tier2')}
                  >
                    <i className={getSortingClass('tier2')}></i>Tier 2
                  </th>
                  <th
                    className='clickable'
                    style={{ width: '15%', textAlign: 'center' }}
                    onClick={() => onColumnHeaderClick('tier3')}
                  >
                    <i className={getSortingClass('tier3')}></i>Tier 3
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered
                  .filter((h) => !!devices.find((d) => d.dUuid === h.dUuid))
                  .map((h) => {
                    const device = devices.find((d) => d.dUuid === h.dUuid)!
                    return (
                      <tr key={h.deviceId}>
                        <td>
                          {device.deviceName} (#{h.deviceId})
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className='btn btn-link te text-decoration-none px-1'
                            onClick={() => {
                              setSelectedHistoricalGroups(
                                h.tier1.map((g) => {
                                  return {
                                    deviceId: h.deviceId,
                                    dlId: h.dlId,
                                    dUuid: h.dUuid,
                                    deviceUom: device.deviceSettings.uom,
                                    timezone: device.deviceSettings.timezone,
                                    deviceLocation: device.deviceName,
                                    priorityTier: 1,
                                    group: g,
                                  }
                                }),
                              )
                            }}
                          >
                            {h.tier1.length}
                          </button>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className='btn btn-link te text-decoration-none px-1'
                            onClick={() => {
                              setSelectedHistoricalGroups(
                                h.tier2.map((g) => {
                                  return {
                                    deviceId: h.deviceId,
                                    dlId: h.dlId,
                                    dUuid: h.dUuid,
                                    deviceUom: device.deviceSettings.uom,
                                    timezone: device.deviceSettings.timezone,
                                    deviceLocation: device.deviceName,
                                    priorityTier: 2,
                                    group: g,
                                  }
                                }),
                              )
                            }}
                          >
                            {h.tier2.length}
                          </button>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className='btn btn-link te text-decoration-none px-1'
                            onClick={() => {
                              setSelectedHistoricalGroups(
                                h.tier3.map((g) => {
                                  return {
                                    deviceId: h.deviceId,
                                    dlId: h.dlId,
                                    dUuid: h.dUuid,
                                    deviceUom: device.deviceSettings.uom,
                                    timezone: device.deviceSettings.timezone,
                                    deviceLocation: device.deviceName,
                                    priorityTier: 3,
                                    group: g,
                                  }
                                }),
                              )
                            }}
                            // disabled={h.tier3.length === 0}
                          >
                            {h.tier3.length}
                          </button>
                        </td>
                      </tr>
                    )
                  })}
              </tbody>
              <tfoot>
                <tr>
                  <td>
                    <b>Total Alerts ({statsTotal.totalAll})</b>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <b>{statsTotal.tier1Total}</b>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <b>{statsTotal.tier2Total}</b>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <b>{statsTotal.tier3Total}</b>
                  </td>
                </tr>
              </tfoot>
            </Table>
          </div>
        </div>
      ) : (
        <div>
          <h5>No Historical Alerts For Selected Accounts</h5>
        </div>
      )}
      <Modal
        show={selectedHistoricalGroups && selectedHistoricalGroups.length > 0}
        onHide={() => {
          setSelectedHistoricalGroups([])
          setSelectedHistoricalGroup(undefined)
        }}
        backdrop='static'
        size='xl'
        aria-labelledby='contained-modal-title-vcenter'
        centered
      >
        <Modal.Header className='modal-header' closeButton>
          <Modal.Title id='contained-modal-title-vcenter'>
            {selectedHistoricalGroups && selectedHistoricalGroups.length
              ? `Tier ${selectedHistoricalGroups[0].priorityTier} Alerts - ${selectedHistoricalGroups[0].deviceLocation}`
              : null}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedHistoricalGroups && selectedHistoricalGroups.length ? (
            <div>
              <Select
                value={selectedHistoricalGroup}
                onChange={(group: any) => setSelectedHistoricalGroup(group)}
                options={selectedHistoricalGroups}
                classNamePrefix='select'
                getOptionValue={(option: any) => option.group}
                getOptionLabel={(option: any) =>
                  `${format(TZDate.tz(option.group.timezone, option.group.startDate), 'EEE, dd/MM/yy h:mm a')} - ${format(TZDate.tz(option.group.timezone, option.group.endDate), 'EEE, dd/MM/yy h:mm a')}`
                }
                isClearable={true}
              />
              {/* AlertQueueItem retains its last group when handed undefined, so the
                  loading, error and empty branches must render instead of it, never
                  alongside it — otherwise the previous group stays on screen. */}
              <div className='mt-1'>
                {!selectedHistoricalGroup ? null
                  : isGroupLoading ? (
                    <div className='spinner-border m-auto mb-5 mt-5 d-flex text-primary' />
                  ) : isGroupError ? (
                    <p className='mt-4 text-center'>Unable to load this alert group</p>
                  ) : selectedAlertGroup ? (
                    <AlertQueueItem alertGroupItem={selectedAlertGroup} historical={true} />
                  ) : (
                    <p className='mt-4 text-center'>No alerts in this group</p>
                  )}
              </div>
            </div>
          ) : null}
        </Modal.Body>
      </Modal>
    </>
  )
}
